import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  Tag,
  Table,
  Alert,
  Spin,
  Divider,
  Progress,
} from "antd";
import {
  RobotOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BulbOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../../utils/axiosInstance";
import DownloadReportButton from "../../../pages/measurement/geo-lift/components/DownloadReportButton";

const { Title, Text, Paragraph } = Typography;

const AIAgentInsights = ({ jobId, isSales }: { jobId: string; isSales?: boolean }) => {
  // When isSales=true the KPI is unit-based (not dollar revenue): hide $ and iROAS.
  const hideDollarMetrics = isSales === true;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    const fetchAIInsights = async () => {
      if (!jobId) return;

      setLoading(true);

      try {
        const response = await axiosInstance.get(`/api/ai/insights/${jobId}`);
        setData(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAIInsights();
  }, [jobId]);

  if (loading)
    return (
      <div className="p-10 text-center">
        <Spin size="large" tip="AI Agent is analyzing..." />
      </div>
    );

  if (!data || data?.qc_openai?.status === "insufficient_data")
    return <Alert message="No AI insights available." type="info" />;

  const { qc_openai } = data;
  const { insights: aiInsights, qc } = qc_openai;

  const {
    summary: aiSummary,
    recommendations: aiRecommendations,
    issues: aiIssues,
  } = aiInsights || {};

  const {
    computed_metrics,
    insights: qcInsights,
    issues: qcIssues,
  } = qc || {};

  const {
    summary: qcSummary,
    recommendations: qcRecommendations,
  } = qcInsights || {};

  const issues = [...(aiIssues || []), ...(qcIssues || [])];

  return (
    
    <div className="max-w-5xl mx-auto py-6 space-y-6">

      {/* 🔹 HEADER ROW */}
      <div className="flex justify-between items-center">
        <Title level={4} className="m-0">
          AI Insights
        </Title>

        <DownloadReportButton
          job_config_id={jobId}
          buttonClassName="w-full"
          buttonProps={{
            size: "large",
            block: true,
          }}
        />
      </div>

      {/* 🔹 AI SUMMARY */}
      <AISummaryStrategySection
        title="AI Summary & Strategy"
        icon={<RobotOutlined className="text-[var(--blue)]" />}
        summary={aiSummary}
        recommendations={aiRecommendations}
        gradient="from-[var(--blue-subtle)] to-[var(--bg-elevated)]"
      />

      {qc && ["dev", "staging"].includes(process.env.REACT_APP_ENV ?? "dev") && (
        <Card
          className="shadow-lg border-0 rounded-2xl"
          title={
            <span className="flex items-center gap-2 text-[var(--success)] font-semibold">
              <CheckCircleOutlined />
              AI Quality Control
            </span>
          }
        >
          <AISummaryStrategySection
            title="QC Strategic Insights"
            icon={<RobotOutlined className="text-[var(--success)]" />}
            summary={qcSummary}
            recommendations={qcRecommendations}
            gradient="from-[var(--success-subtle)] to-[var(--bg-elevated)]"
          />

          <Divider />

          <div className={`grid grid-cols-1 ${hideDollarMetrics ? "md:grid-cols-2" : "md:grid-cols-3"} gap-4 mb-8`}>
            <MetricCard
              label="Aggregate Lift"
              value={`${computed_metrics?.aggregate_lift_percentage || 0}%`}
              color="text-[var(--blue)]"
            />
            {!hideDollarMetrics && (
              <MetricCard
                label="iROAS"
                value={`${computed_metrics?.iroas || 0}x`}
                color="text-[var(--success)]"
              />
            )}
            <MetricCard
              label="Incremental Sales"
              value={hideDollarMetrics
                ? `${Math.round(computed_metrics?.total_sales_increment || 0).toLocaleString()}`
                : `$${Math.round(computed_metrics?.total_sales_increment || 0).toLocaleString()}`
              }
              color="text-[var(--violet)]"
            />
          </div>

          {issues.length > 0 && (
            <>
              <Title level={5} className="text-[var(--error)]">
                <WarningOutlined /> Detected Issues
              </Title>

              <div className="space-y-2 mb-6">
                {issues.map((issue: string, i: number) => (
                  <Alert key={i} message={issue} type="error" showIcon />
                ))}
              </div>
            </>
          )}

          <Title level={5}>Market Performance</Title>

          <Table
            pagination={false}
            size="middle"
            dataSource={Object.entries(computed_metrics?.markets || {}).map(
              ([name, val]: any) => ({
                key: name,
                market: name,
                ...val,
              })
            )}
            columns={[
              {
                title: "Market",
                dataIndex: "market",
                render: (text) => (
                  <span className="font-semibold text-[var(--text-primary)]">{text}</span>
                ),
              },
              {
                title: "Lift",
                dataIndex: "lift_percentage",
                render: (val) => (
                  <Tag color={val > 5 ? "green" : "orange"}>{val}%</Tag>
                ),
              },
              {
                title: "Confidence",
                dataIndex: "confidence",
                render: (val) => (
                  <div className="w-32">
                    <Progress percent={Math.round(val)} size="small" />
                  </div>
                ),
              },
              {
                title: "Duration",
                render: (row: any) => (
                  <Text type="secondary">
                    {row.start_campaign_date} → {row.end_campaign_date}
                  </Text>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

const AISummaryStrategySection = ({
  title,
  icon,
  summary,
  recommendations,
  gradient,
}: any) => {
  const recs = Array.isArray(recommendations) ? recommendations : [];

  const budget = recs.find((r: any) => r.type === "budget_allocation");
  const market = recs.find((r: any) => r.type === "market_strategy");
  const learning = recs.find((r: any) =>
    ["next_learning_agenda", "next_learning_agenda_experiments"].includes(
      r.type
    )
  );

  const budgetLines = parseLines(budget?.message);
  const marketLines = parseLines(market?.message);
  const learningSections = parseLearningAgendaMessage(learning?.message);

  return (
    <Card
      className={`shadow-lg border-0 rounded-2xl bg-gradient-to-r ${gradient}`}
      title={
        <span className="text-lg font-semibold flex items-center gap-2">
          {icon}
          {title}
        </span>
      }
    >
      {summary && (
        <Paragraph className="text-lg text-[var(--text-primary)] italic bg-[var(--bg-elevated)] p-4 rounded-lg shadow-sm">
          "{summary}"
        </Paragraph>
      )}

      {(budgetLines.length > 0 || marketLines.length > 0) && (
        <>
          <Divider>Recommendations</Divider>

          <div className="grid md:grid-cols-2 gap-4">
            {budgetLines.length > 0 && (
              <GroupedRecommendationCard
                type="budget_allocation"
                title="Budget Allocation"
                items={budgetLines}
              />
            )}

            {marketLines.length > 0 && (
              <GroupedRecommendationCard
                type="market_strategy"
                title="Market Strategy"
                items={marketLines}
              />
            )}
          </div>
        </>
      )}

      {learningSections.length > 0 && (
        <>
          <Divider>Next Learning Agenda Experiments</Divider>

          <div className="grid md:grid-cols-3 gap-4">
            {learningSections.map((section: any, index: number) => (
              <div
                key={index}
                className="p-4 bg-[var(--bg-elevated)] rounded-xl shadow-sm border border-[var(--border-subtle)]"
              >
                <div className="flex items-center gap-2 mb-2">
                  <BulbOutlined className="text-[var(--warning)]" />
                  <Tag color="purple">{section.title}</Tag>
                </div>

                <Text className="text-[var(--text-primary)]">{section.message}</Text>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

const GroupedRecommendationCard = ({ type, title, items }: any) => (
  <div className="p-4 bg-[var(--bg-elevated)] rounded-xl shadow-sm border border-[var(--border-subtle)]">
    <div className="flex items-center gap-2 mb-3">
      <BulbOutlined className="text-[var(--warning)]" />
      <Tag color="blue">{title || formatTypeLabel(type)}</Tag>
    </div>

    <ul className="list-disc pl-5 space-y-2 m-0">
      {items.map((item: string, index: number) => (
        <li key={index}>
          <Text className="text-[var(--text-primary)]">{item}</Text>
        </li>
      ))}
    </ul>
  </div>
);

const RecommendationCard = ({ type, message }: any) => (
  <div className="p-4 bg-[var(--bg-elevated)] rounded-xl shadow-sm border border-[var(--border-subtle)]">
    <div className="flex items-center gap-2 mb-2">
      <BulbOutlined className="text-[var(--warning)]" />
      <Tag color="blue">{formatTypeLabel(type)}</Tag>
    </div>

    <Text className="text-[var(--text-primary)]">{message}</Text>
  </div>
);

const InsightSection = ({
  title,
  icon,
  summary,
  recommendations,
  gradient,
}: any) => (
  <Card
    className={`shadow-lg border-0 rounded-2xl bg-gradient-to-r ${gradient}`}
    title={
      <span className="text-lg font-semibold flex items-center gap-2">
        {icon}
        {title}
      </span>
    }
  >
    {summary && (
      <Paragraph className="text-lg text-[var(--text-primary)] italic bg-[var(--bg-elevated)] p-4 rounded-lg shadow-sm">
        "{summary}"
      </Paragraph>
    )}

    {Array.isArray(recommendations) && recommendations.length > 0 && (
      <>
        <Divider>Recommendations</Divider>

        <div className="grid md:grid-cols-2 gap-4">
          {recommendations.map((rec: any, index: number) => (
            <RecommendationCard key={index} {...rec} />
          ))}
        </div>
      </>
    )}
  </Card>
);

const MetricCard = ({ label, value, color }: any) => (
  <div className="bg-[var(--bg-elevated)] p-5 rounded-xl shadow-sm hover:shadow-md transition text-center border border-[var(--border-subtle)]">
    <Text className="text-xs uppercase text-[var(--text-secondary)] tracking-wide">
      {label}
    </Text>

    <Title level={3} className={`m-0 ${color}`}>
      {value}
    </Title>
  </div>
);

const formatTypeLabel = (type: string = "") =>
  type
    .split("_")
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const parseLines = (message: string = "") =>
  message
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const parseLearningAgendaMessage = (message: string = "") => {
  if (!message) return [];

  const titles = [
    "X-Channel Incrementality",
    "Halo Impact",
    "Optimal Marginal Investment",
  ];

  return titles
    .map((title, index) => {
      const nextTitle = titles[index + 1];

      const regex = nextTitle
        ? new RegExp(`${title}:\\s*([\\s\\S]*?)(?=${nextTitle}:)`, "i")
        : new RegExp(`${title}:\\s*([\\s\\S]*)`, "i");

      const match = message.match(regex);

      return {
        title,
        message: match?.[1]?.trim() || "",
      };
    })
    .filter((section) => section.message);
};

export default AIAgentInsights;